import * as React from 'react'
import {
  CustomerAgencyModel,
  StackHealth,
  CustomerAgencyInterfaceHealthStatuses,
} from '../api'
import { LoadingState, Customer, LoadingStatus } from './base'
import { idObjectToArray, setCustomerHealth } from 'utils'

export type Customers = {
  [key: string]: Customer
}

export interface CustomersState {
  customers: Customers
  customersList: Customer[]
  loadingState: LoadingState
  hasLoadedHealth: boolean
}

type LoadCustomersAction = {
  type: 'LoadCustomers'
  payload: CustomerAgencyModel[]
  timestamp: number | null
}

type SetCustomerAgencyInterfaceHealthStatuses = {
  type: 'SetCustomerAgencyInterfaceHealthStatuses'
  payload: CustomerAgencyInterfaceHealthStatuses
}

type EditCustomerAction = {
  type: 'EditCustomer'
  payload: Customer
}

type UpdateCustomerSummaryAction = {
  type: 'UpdateCustomerSummary'
  payload: CustomerAgencyModel
}

type AddCustomerAction = {
  type: 'AddCustomer'
  payload: Customer
}

type DeleteCustomerAction = {
  type: 'DeleteCustomer'
  payload: string
}

type ClearAction = {
  type: 'Clear'
}

type SetLoadingState = {
  type: 'SetLoadingState'
  payload: LoadingStatus
}

export type CustomerAction =
  | LoadCustomersAction
  | EditCustomerAction
  | DeleteCustomerAction
  | AddCustomerAction
  | ClearAction
  | SetCustomerAgencyInterfaceHealthStatuses
  | SetLoadingState
  | UpdateCustomerSummaryAction

export interface CustomersContextState {
  state: CustomersState
  dispatch: (action: CustomerAction) => void
}

const DEFAULT_CUSTOMERS_STATE: CustomersContextState = {
  state: {
    customers: {},
    customersList: [],
    loadingState: {
      status: 'None',
      lastApiReceiveAt: null,
    },
    hasLoadedHealth: false,
  } as CustomersState,
  dispatch: (action: CustomerAction) => {},
}

const reducer = (state: CustomersState, action: CustomerAction) => {
  switch (action.type) {
    case 'Clear': {
      return DEFAULT_CUSTOMERS_STATE.state
    }
    case 'SetLoadingState': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          status: action.payload,
        },
      }
    }
    case 'LoadCustomers': {
      const customers = action.payload.reduce((accum, current) => {
        accum[current.customerAgencyId] = {
          ...current,
          id: current.customerAgencyId,
        }

        return accum
      }, {} as Customers)
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
        loadingState: {
          status: 'None',
          lastApiReceiveAt: action.timestamp,
        },
      }
    }
    case 'SetCustomerAgencyInterfaceHealthStatuses': {
      const updatedCustomersList = setCustomerHealth(
        action.payload,
        state.customersList,
      )

      // don't actually do any updates if there were no changes
      if (state.customersList === updatedCustomersList) return state

      const customers = updatedCustomersList.reduce((accum, current) => {
        accum[current.customerAgencyId] = {
          ...current,
          id: current.customerAgencyId,
        }

        return accum
      }, {} as Customers)
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
        hasLoadedHealth: true,
      }
    }
    case 'EditCustomer': {
      const customers = {
        ...state.customers,
        [action.payload.id]: action.payload,
      }
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
      }
    }
    case 'UpdateCustomerSummary': {
      const customer = {
        ...state.customers[action.payload.customerAgencyId],
        ...action.payload,
      }
      const customers = {
        ...state.customers,
        [action.payload.customerAgencyId]: customer,
      }
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
      }
    }
    case 'AddCustomer': {
      const customers = {
        ...state.customers,
        [action.payload.id]: action.payload,
      }
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
      }
    }
    case 'DeleteCustomer': {
      const customers = Object.keys(state.customers)
        .filter(key => key !== action.payload)
        .reduce((accum, current) => {
          return {
            ...accum,
            [current]: state.customers[current],
          }
        }, {})
      return {
        ...state,
        customers,
        customersList: idObjectToArray(customers),
      }
    }
    default:
      return state
  }
}

export const CustomersContext = React.createContext<CustomersContextState>(
  DEFAULT_CUSTOMERS_STATE,
)

type CustomersContextProviderProps = {
  children: any
}

export const CustomersContextProvider: React.FunctionComponent<CustomersContextProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(
    reducer as React.Reducer<CustomersState, CustomerAction>,
    DEFAULT_CUSTOMERS_STATE.state,
  )
  const value = { state, dispatch }

  return (
    <CustomersContext.Provider value={value}>
      {children}
    </CustomersContext.Provider>
  )
}

export const CustomersContextConsumer = CustomersContext.Consumer
